/**
 * 운영자 고객 상세의 「고객 정보」 — 보기와 편집이 한 카드다 (운영자 결정 2026-10-11).
 * 예전에는 목록의 편집 창(모달)·상세·브랜드 프로필 카드가 갈려 있었다 — 상세의 이 카드 하나에서 보고, 「편집」을 눌러 고친다.
 *
 * ★ **기본은 보기 모드다.** 입력란은 「편집」을 눌러야 열린다 — 상세를 둘러보다 실수로 판정 브랜드명을 고치지 않게.
 * ★ **운영자 전용이다.** `BrandDetailPage` 는 `/app` 에도 걸린 공용 화면이라 부르는 쪽이 `isAdmin` 일 때만 마운트한다.
 * ★ 신청자의 요청값(요청 브랜드명·원하는 질문·남긴 말)은 **가입 신청 화면에서만** 보인다 — 승인이 곧 확정이고, 여기는 확정된 값만 다룬다.
 *   승인도 가입 신청 화면에서만 한다(브랜드명·질문을 확정해 등록해야 승인된다 — 운영자 결정 2026-10-11).
 * ★ 브랜드 프로필은 고객이 준 사실의 원천(SSOT, 261008-client-is-brand) — 자유 마크다운, 레포로는 `targets-export` 가 내보낸다.
 */
import { auth, registry } from '@geo-admin/shared/contract/api/index.ts';
import { Link } from '@tanstack/react-router';
import type { BrandDetail } from '@geo-admin/shared/contract/api/registry.ts';
import { PROFILE_MAX_CHARS } from '@geo-admin/shared/contract/api/registry.ts';
import { useState } from 'react';
import { useApi, useApiMutation } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';
import { PROFILE_TEMPLATES } from '../lib/profileTemplates.ts';
import { useImpersonate } from '../lib/useImpersonate.ts';
import { LoginHistoryDialog } from './LoginHistory.tsx';
import { LinksField, type LinksValue, linkKind, linksFrom, linksPayload } from './LinksField.tsx';
import { Button } from './ui/button.tsx';
import { Badge, Card, CardBody, CardHeader } from './ui/card.tsx';
import { FieldError, Input, Label, Select, Textarea } from './ui/form.tsx';

const lines = (v: string) =>
  v
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

export function BrandInfoAdmin({ brand: c }: { brand: BrandDetail }) {
  const [editing, setEditing] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);

  return (
    <Card>
      <CardHeader
        title="브랜드 정보"
        description={`만든 날 ${dateTime(c.createdAt)}${c.approvedAt ? ` · 승인 ${dateTime(c.approvedAt)}` : ''}`}
        actions={
          <div className="flex items-center gap-2">
            {c.status === 'pending' ? (
              <Badge tone="amber">승인 대기</Badge>
            ) : c.status === 'confirmed' ? (
              <Badge tone="blue">결제 대기</Badge>
            ) : (
              <Badge tone="green">운영 중</Badge>
            )}
            {c.status === 'pending' ? (
              <Link to="/admin/signups" className="text-live text-sm hover:underline">
                가입 신청에서 승인
              </Link>
            ) : null}
            {editing ? null : (
              <Button
                size="sm"
                onClick={() => {
                  setWarnings([]);
                  setEditing(true);
                }}
              >
                편집
              </Button>
            )}
          </div>
        }
      />
      {editing ? (
        <EditForm
          brand={c}
          onCancel={() => setEditing(false)}
          onSaved={(w) => {
            setWarnings(w);
            setEditing(false);
          }}
        />
      ) : (
        <View brand={c} />
      )}
      {warnings.length ? (
        <CardBody className="space-y-1 border-slate-100 border-t text-sm">
          {warnings.map((w) => (
            <p key={w} className="text-amber-800">
              ⚠ {w}
            </p>
          ))}
        </CardBody>
      ) : null}
    </Card>
  );
}

// ── 보기 ────────────────────────────────────────────────────────────────

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-0.5 sm:grid-cols-[140px_1fr] sm:gap-3">
      <div className="text-slate-500 text-xs sm:pt-0.5">{label}</div>
      <div className="min-w-0 text-sm">{children}</div>
    </div>
  );
}

const none = <span className="text-slate-400">없음</span>;

function UrlLink({ url }: { url: string }) {
  const kind = linkKind(url);
  return (
    <div className="flex min-w-0 items-center gap-2">
      <a href={url} target="_blank" rel="noreferrer" className="truncate text-live hover:underline">
        {url}
      </a>
      {kind ? <span className="shrink-0 text-slate-400 text-xs">{kind}</span> : null}
    </div>
  );
}

function View({ brand: c }: { brand: BrandDetail }) {
  return (
    <CardBody className="grid gap-3">
      <Row label="이름">{c.name}</Row>
      <Row label="목표 브랜드명">
        {c.brandNames.length ? c.brandNames.join(' · ') : <span className="text-amber-700">없음</span>}
      </Row>
      <Row label="대표 웹페이지">{c.homepageUrl ? <UrlLink url={c.homepageUrl} /> : none}</Row>
      <Row label="네이버 플레이스">{c.naverPlaceUrl ? <UrlLink url={c.naverPlaceUrl} /> : none}</Row>
      <Row label="기타 링크">
        {c.links.length ? (
          <div className="grid gap-0.5">
            {c.links.map((l) => (
              <UrlLink key={l} url={l} />
            ))}
          </div>
        ) : (
          none
        )}
      </Row>
      <Row label="브랜드 프로필">
        {c.profile.trim() ? (
          <div>
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 font-mono text-[13px] leading-relaxed">
              {c.profile}
            </pre>
            <p className="mt-1 text-slate-400 text-xs">
              고객이 준 사실의 원천 — 공공 자료(심평원·네이버)보다 우선합니다 · 마지막 수정{' '}
              {dateTime(c.profileUpdatedAt)}
            </p>
          </div>
        ) : (
          none
        )}
      </Row>
    </CardBody>
  );
}

// ── 편집 ────────────────────────────────────────────────────────────────

function EditForm({
  brand: c,
  onCancel,
  onSaved,
}: {
  brand: BrandDetail;
  onCancel: () => void;
  onSaved: (warnings: string[]) => void;
}) {
  const update = useApiMutation(registry.updateBrand);
  const [name, setName] = useState(c.name);
  const [brandNames, setBrandNames] = useState(c.brandNames.join('\n'));
  const [links, setLinks] = useState<LinksValue>(() => linksFrom(c));
  const [profile, setProfile] = useState(c.profile);
  const [error, setError] = useState<string | null>(null);

  const p = linksPayload(links);
  const names = lines(brandNames);
  const dirty =
    name.trim() !== c.name ||
    names.join('\n') !== c.brandNames.join('\n') ||
    (p.homepageUrl || null) !== c.homepageUrl ||
    p.naverPlaceUrl !== c.naverPlaceUrl ||
    p.links.join('\n') !== c.links.join('\n') ||
    profile !== c.profile;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!dirty) {
      onCancel();
      return;
    }
    try {
      const r = await update.mutateAsync({
        params: { id: c.id },
        body: {
          name: name.trim(),
          homepageUrl: p.homepageUrl || null,
          naverPlaceUrl: p.naverPlaceUrl,
          links: p.links,
          // 비워 두면 보내지 않는다 — 브랜드명은 지울 수 없다(질문 판정이 복사해 쓴다)
          ...(names.length > 0 ? { brandNames: names } : {}),
          // 본문이 실제로 바뀐 때만 보낸다 — 서버가 profile_updated_at 을 그때만 민다
          ...(profile !== c.profile ? { profile } : {}),
        },
      });
      onSaved(r.warnings);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <form onSubmit={(e) => void save(e)}>
      <CardBody className="grid gap-5 lg:grid-cols-2">
        <div className="grid content-start gap-3">
          <Label>
            이름
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required />
          </Label>
          <Label>
            목표 브랜드명 (한 줄에 하나 · 첫 줄이 대표 표기 · 띄어쓰기·대소문자 차이는 자동으로 잡습니다 — 영문·약칭처럼
            글자가 다른 표기만 더해 주세요)
            <Textarea rows={3} value={brandNames} onChange={(e) => setBrandNames(e.target.value)} />
            <span className="block text-slate-500 text-xs">
              이 고객의 질문 전부가 이 이름으로 판정됩니다. 바꾸면 과거 샘플도 다시 판정됩니다.
            </span>
          </Label>
        </div>
        <LinksField value={links} onChange={setLinks} required={false} />
      </CardBody>
      <CardBody className="grid gap-2 border-slate-100 border-t">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-medium text-sm">브랜드 프로필</span>
          {profile.trim() === '' ? (
            <div className="flex gap-2">
              {PROFILE_TEMPLATES.map((t) => (
                <Button key={t.id} type="button" size="sm" variant="outline" onClick={() => setProfile(t.body)}>
                  {t.label} 틀로 시작
                </Button>
              ))}
            </div>
          ) : null}
        </div>
        <Textarea
          value={profile}
          onChange={(e) => setProfile(e.target.value)}
          rows={16}
          maxLength={PROFILE_MAX_CHARS}
          className="font-mono text-sm"
          placeholder="고객이 알려 준 의료진·장비·진료시간·가격·강조점을 마크다운으로 적습니다. 받은 경로(언제·누가·어떻게)를 함께 남겨 주세요."
        />
        <p className="text-slate-400 text-xs">
          {profile.length.toLocaleString()} / {PROFILE_MAX_CHARS.toLocaleString()}자 · 고객이 준 사실의 원천 — 공공
          자료보다 우선합니다. 레포에는 `targets-export` 로 내보냅니다(사이트의 content/brand-profile.md)
        </p>
      </CardBody>
      <CardBody className="flex items-center justify-end gap-2 border-slate-100 border-t">
        <FieldError>{error}</FieldError>
        <Button type="button" variant="outline" onClick={onCancel} disabled={update.isPending}>
          취소
        </Button>
        <Button type="submit" disabled={update.isPending || !name.trim()}>
          {update.isPending ? '저장 중' : '저장'}
        </Button>
      </CardBody>
    </form>
  );
}

// ── 계정 ────────────────────────────────────────────────────────────────

/**
 * 이 브랜드의 소속 계정과 역할 (261013-account-brand-membership) — 역할 바꾸기·빼기·대리 로그인.
 * 마지막 소유자는 빼거나 내릴 수 없다(서버 409). 계정 발급·다른 브랜드 소속 추가는 「계정」 화면에서 한다
 */
export function BrandAccountsAdmin({ brandId }: { brandId: string }) {
  const members = useApi(registry.listBrandMembersAdmin, { params: { id: brandId } });
  const setRole = useApiMutation(auth.updateMembership);
  const remove = useApiMutation(auth.removeMembership);
  const impersonate = useImpersonate();
  const list = members.data?.members ?? [];
  // 원장님이 실제로 들어오는지 — 로그인 한 번이 한 줄(LoginHistory)
  const [history, setHistory] = useState<{ id: string; email: string } | null>(null);
  return (
    <Card>
      <CardHeader
        title="소속 계정"
        description="소유자는 사람을 초대·내보내고 승인 전 브랜드를 지웁니다 · 계정 발급은 「계정」 화면에서"
      />
      <CardBody className="grid gap-2 text-sm">
        {members.isLoading ? null : members.error ? (
          // ★ 조회 실패를 '계정 없음' 으로 흘리지 않는다 — 운영자가 중복 계정을 발급하게 된다
          <span className="text-bad">계정을 불러오지 못했습니다</span>
        ) : list.length === 0 ? (
          <span className="text-amber-700">소속 계정 없음 — 주인 없는 브랜드입니다</span>
        ) : (
          <ul className="divide-y divide-slate-100">
            {list.map((m) => (
              <li key={m.membershipId} className="flex flex-wrap items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <div className="truncate">{m.email}</div>
                  <div className="text-slate-500 text-xs">
                    {[m.name, m.companyName].filter(Boolean).join(' · ')}
                    {m.name || m.companyName ? ' · ' : ''}
                    {dateTime(m.since)}부터
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={m.role}
                    disabled={setRole.isPending}
                    onChange={(e) =>
                      setRole.mutate({
                        params: { id: m.membershipId },
                        body: { role: e.target.value as 'owner' | 'manager' },
                      })
                    }
                  >
                    <option value="owner">소유자</option>
                    <option value="manager">관리자</option>
                  </Select>
                  <Button size="sm" variant="outline" onClick={() => setHistory({ id: m.userId, email: m.email })}>
                    로그인 기록
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => void impersonate(m.userId)}>
                    대리 로그인
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={remove.isPending}
                    onClick={() =>
                      confirm(`${m.email} 을(를) 이 브랜드에서 뺍니다`) &&
                      remove.mutate({ params: { id: m.membershipId } })
                    }
                  >
                    빼기
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <FieldError>{(setRole.error ?? remove.error)?.message}</FieldError>
      </CardBody>
      <LoginHistoryDialog user={history} onClose={() => setHistory(null)} />
    </Card>
  );
}

// ── 질문 추가 ────────────────────────────────────────────────────────────

/**
 * 고객 상세에서 질문을 바로 등록한다 — 질문 관리(`/admin/queries`)와 같은 계약(`createQuery`)이다.
 * 문구와 하루 샘플 수만 받는다. 온보딩은 서버 기본값이고, 세부 설정은 질문 관리에서 고친다.
 * ★ 브랜드명은 받지 않는다 — 고객의 목표 브랜드명을 복사한다(261008-client-is-brand). 비었으면 서버도 400 이다.
 * ★ 문구는 만든 뒤 바꿀 수 없다(260919-query-text-immutable) — 저장 전에 한 번 더 보게 한다.
 */
export function AddQueryAdmin({ brand: c }: { brand: BrandDetail }) {
  const create = useApiMutation(registry.createQuery);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [samplesPerDay, setSamplesPerDay] = useState(10);
  const [warnings, setWarnings] = useState<string[]>([]);
  const noBrand = c.brandNames.length === 0;

  if (!open)
    return (
      <div className="flex items-center gap-2">
        {noBrand ? <span className="text-amber-700 text-xs">목표 브랜드명을 먼저 정해 주세요</span> : null}
        {warnings.map((w) => (
          <span key={w} className="text-amber-800 text-xs">
            ⚠ {w}
          </span>
        ))}
        <Button
          size="sm"
          disabled={noBrand}
          onClick={() => {
            setWarnings([]);
            setOpen(true);
          }}
        >
          질문 추가
        </Button>
      </div>
    );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      { body: { brandId: c.id, query: query.trim(), samplesPerDay } },
      {
        onSuccess: (r) => {
          setWarnings(r.warnings);
          setQuery('');
          setSamplesPerDay(10);
          setOpen(false);
        },
      },
    );
  };

  return (
    <form className="flex w-full flex-wrap items-end gap-2" onSubmit={submit}>
      <Label className="min-w-64 flex-1">
        질문 <span className="font-normal text-slate-500 text-xs">만든 뒤에는 문구를 바꿀 수 없습니다</span>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          maxLength={300}
          placeholder="예: 강남 임플란트 치과 추천"
        />
      </Label>
      <Label className="w-28">
        하루 샘플 수
        <Input
          type="number"
          min={0}
          max={48}
          value={samplesPerDay}
          onChange={(e) => setSamplesPerDay(Number(e.target.value))}
        />
      </Label>
      <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={create.isPending}>
        취소
      </Button>
      <Button type="submit" disabled={create.isPending || query.trim().length < 2}>
        등록
      </Button>
      <FieldError>{create.error?.message}</FieldError>
    </form>
  );
}
